import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import AdminPortalLayout from './AdminPortalLayout';
import {
  AdminMe, fetchBulkEmailPreview, sendBulkEmail,
} from '../lib/admin-portal-api';
import { FaCheckCircle, FaExclamationTriangle, FaUserPlus } from '../lib/icons';

interface Props { me: AdminMe }

const AUDIENCES: { id: string; label: string; desc: string }[] = [
  { id: 'all',         label: 'All users',     desc: 'Every registered user' },
  { id: 'premium',     label: 'Premium',       desc: 'is_premium = true' },
  { id: 'free',        label: 'Free',          desc: 'is_premium = false' },
  { id: 'active',      label: 'Active subs',   desc: 'subscription_status = active' },
  { id: 'canceled',    label: 'Canceled subs', desc: 'subscription_status = canceled' },
  { id: 'expired',     label: 'Expired subs',  desc: 'subscription_status = expired' },
  { id: 'unconfirmed', label: 'Unconfirmed',   desc: 'confirmed = false' },
];

export default function AdminPortalBulkEmail({ me }: Props) {
  const [audience, setAudience] = useState('premium');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState<{ total: number; sample: any[] } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setLoadingPreview(true);
    fetchBulkEmailPreview(audience)
      .then((res) => setPreview({ total: res.total, sample: res.sample }))
      .catch(() => setPreview({ total: 0, sample: [] }))
      .finally(() => setLoadingPreview(false));
  }, [audience]);

  if (!me.isSuperAdmin) return <Navigate to="/admin-portal" replace />;

  const onSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (!subject.trim() || !body.trim()) {
      setMsg({ type: 'error', text: 'Subject and body are required.' });
      return;
    }
    if (!preview || preview.total === 0) {
      setMsg({ type: 'error', text: 'No recipients in this audience.' });
      return;
    }
    if (confirm !== 'SEND') {
      setMsg({ type: 'error', text: 'Type SEND to confirm.' });
      return;
    }
    setSending(true);
    try {
      const res = await sendBulkEmail({
        subject: subject.trim(),
        htmlBody: body,
        audience,
        confirmText: 'SEND',
      });
      setMsg({ type: 'success', text: `Sent ${res.sent} / ${res.total} · failed ${res.failed}.` });
      setSubject(''); setBody(''); setConfirm('');
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.error || 'Send failed.' });
    } finally { setSending(false); }
  };

  return (
    <AdminPortalLayout me={me} active="bulk-email">
      <Helmet><title>Bulk Email — EatMeat Admin</title></Helmet>

      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Bulk Email</h1>
        <p className="text-sm text-gray-500 mt-1">Personalized merge fields: <code>{'{{username}}'}</code> · <code>{'{{email}}'}</code>.</p>
      </div>

      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm flex items-start gap-2 mb-4 ${
          msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800'
                                 : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {msg.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
          {msg.text}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <form onSubmit={onSend} className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="font-bold text-gray-800 mb-3">Audience</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {AUDIENCES.map((a) => (
                <button key={a.id} type="button" onClick={() => setAudience(a.id)}
                  className={`text-left rounded-xl p-3 border transition-colors ${
                    audience === a.id ? 'border-primary bg-primary/5' : 'border-gray-100 hover:border-gray-300'
                  }`}>
                  <div className={`text-sm font-semibold ${audience === a.id ? 'text-primary' : 'text-gray-800'}`}>{a.label}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{a.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
            <h3 className="font-bold text-gray-800">Compose</h3>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} required
                placeholder="A friendly subject line"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Body (HTML supported)</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={12}
                placeholder={`Hi {{username}},\n\nWe wanted to let you know…\n\n— The EatMeat team`}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none font-mono" />
              <p className="text-[10px] text-gray-400 mt-1">HTML tags render in the email; plain text works too. <code>{'{{username}}'}</code> and <code>{'{{email}}'}</code> are substituted per recipient.</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border-2 border-red-100 shadow-sm p-5">
            <h3 className="font-bold text-gray-800 mb-2">Confirm + send</h3>
            <p className="text-[11px] text-gray-500 mb-3">
              This will send <strong>{preview?.total ?? '…'}</strong> email{(preview?.total || 0) === 1 ? '' : 's'} immediately. Type <code className="bg-gray-100 px-1 rounded">SEND</code> below to confirm.
            </p>
            <div className="flex gap-3 items-end">
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="SEND"
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none w-32 font-mono" />
              <button type="submit" disabled={sending || confirm !== 'SEND' || !preview || preview.total === 0}
                className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
                {sending ? 'Sending…' : `Send to ${preview?.total ?? 0}`}
              </button>
            </div>
          </div>
        </form>

        {/* Sample recipients */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 h-fit">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-800 inline-flex items-center gap-2"><FaUserPlus className="text-blue-500" /> Sample</h3>
            <span className="text-[11px] text-gray-500">
              {loadingPreview ? '…' : preview?.total ?? 0} total
            </span>
          </div>
          {loadingPreview ? (
            <div className="text-sm text-gray-400">Counting…</div>
          ) : !preview || preview.sample.length === 0 ? (
            <div className="text-sm text-gray-400">No matching users.</div>
          ) : (
            <ul className="space-y-2">
              {preview.sample.map((u) => (
                <li key={u.id} className="text-xs">
                  <div className="font-semibold text-gray-700 truncate">{u.username || '—'}</div>
                  <div className="text-gray-500 truncate">{u.email}</div>
                </li>
              ))}
              {preview.total > preview.sample.length && (
                <li className="text-[10px] text-gray-400">… and {preview.total - preview.sample.length} more</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </AdminPortalLayout>
  );
}
